const moment = require('moment-timezone');
const { normalizeRefundPolicy } = require('./refund-policy');
const { refundTotal, settledGuestCharges } = require('./guest-charges');
const { REQUIREMENT, STATUS } = require('./guest-stay-state');
const { balanceDueIsInFuture } = require('./payment-terms');

const ELIGIBILITY_STATUSES = [
  'full_until_check_in',
  'full_until_deadline',
  'past_deadline',
  'non_refundable',
  'custom'
];

const parseHexColor = color => {
  if (typeof color !== 'string') return null;
  const match = color.trim().match(/^#([\da-f]{3}|[\da-f]{6})$/i);
  if (!match) return null;
  const value =
    match[1].length === 3
      ? match[1]
          .split('')
          .map(char => char + char)
          .join('')
      : match[1];
  return [0, 2, 4].map(index => parseInt(value.slice(index, index + 2), 16));
};

const mixColor = (color, target, amount) =>
  color.map(channel => Math.round(channel * (1 - amount) + target * amount));

const colorLuminance = color => {
  const linear = color.map(channel => {
    const value = channel / 255;
    return value <= 0.04045 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
  });
  return linear[0] * 0.2126 + linear[1] * 0.7152 + linear[2] * 0.0722;
};

const receiptTheme = (secondaryColor, primaryColor) => {
  const secondary = parseHexColor(secondaryColor) || [121, 64, 255];
  const primary = parseHexColor(primaryColor) || [6, 123, 194];
  let text = secondary;
  let primaryText = primary;
  let deep = mixColor(secondary, 0, 0.35);
  while (1.05 / (colorLuminance(text) + 0.05) < 4.8) {
    text = mixColor(text, 0, 0.05);
  }
  while (1.05 / (colorLuminance(primaryText) + 0.05) < 4.8) {
    primaryText = mixColor(primaryText, 0, 0.05);
  }
  while (1.05 / (colorLuminance(deep) + 0.05) < 9) {
    deep = mixColor(deep, 0, 0.05);
  }
  const cssColor = color => `rgb(${color.join(', ')})`;
  const luminance = colorLuminance(primary);
  return {
    '--receipt-secondary-text': cssColor(text),
    '--receipt-primary-link': cssColor(primaryText),
    '--receipt-secondary-deep': cssColor(deep),
    '--receipt-secondary-deep-rgb': deep.join(', '),
    '--receipt-secondary-tint': cssColor(mixColor(secondary, 255, 0.93)),
    '--receipt-pay-text': luminance > 0.179 ? '#000000' : '#ffffff'
  };
};

const verificationPrompts = ({
  isRvFleet,
  policy,
  authenticateEnabled,
  attempted,
  showDriver
} = {}) => {
  const authenticate = Boolean(authenticateEnabled && !attempted);
  return {
    insurance: Boolean(
      isRvFleet && typeof policy === 'string' && policy.slice(0, 4) === '01A3'
    ),
    driver: Boolean(authenticate && showDriver),
    medallion: Boolean(authenticate && !isRvFleet)
  };
};

const receiptPlaceholders = receipt => {
  const eligibility = receipt && receipt.refund_eligibility;
  const policy = normalizeRefundPolicy(
    receipt && receipt.listing && receipt.listing.refund_policy
  );
  const deposit = receipt && receipt.securityDeposit;
  let cancellation = null;
  if (eligibility != null) {
    if (ELIGIBILITY_STATUSES.includes(eligibility.status)) {
      let deadline = '';
      if (
        eligibility.status === 'full_until_deadline' &&
        typeof eligibility.deadline === 'string' &&
        moment.tz.zone(eligibility.timezone)
      ) {
        const parsed = moment.parseZone(
          eligibility.deadline,
          moment.ISO_8601,
          true
        );
        if (parsed.isValid())
          deadline = parsed.tz(eligibility.timezone).format('MMM D, YYYY');
      }
      if (eligibility.status !== 'full_until_deadline' || deadline) {
        cancellation = { status: eligibility.status, date: deadline };
      }
    }
  } else if (policy === 'full') {
    cancellation = { status: 'full_until_check_in', date: '' };
  } else if (policy === 'no_refund') {
    cancellation = { status: 'non_refundable', date: '' };
  } else if (policy === 'custom' || /^day\d+$/.test(policy || '')) {
    cancellation = { status: 'custom', date: '' };
  }
  return {
    eligibility:
      eligibility && ELIGIBILITY_STATUSES.includes(eligibility.status)
        ? eligibility
        : null,
    cancellation,
    scheduledOn:
      deposit && typeof deposit.scheduled_on === 'string'
        ? deposit.scheduled_on
        : ''
  };
};

const depositCharge = charges => {
  if (!Array.isArray(charges)) return null;
  const latest = charges
    .filter(charge => charge && charge.is_security_deposit === true)
    .slice()
    .reverse()[0];
  return latest &&
    ['auth', 'charged', 'payout', 'partially_refunded'].includes(latest.status)
    ? latest
    : null;
};

const depositAmount = (receipt = {}, depositState) => {
  const deposit = receipt.securityDeposit || {};
  const charge = depositCharge(receipt.charges);
  const candidates = [
    charge && charge.amount_charged,
    deposit.amount,
    deposit.calculation_amount,
    depositState && depositState.amount
  ];
  const value = candidates.find(
    item =>
      item !== null &&
      item !== undefined &&
      item !== '' &&
      Number.isFinite(Number(item))
  );
  return value === undefined ? null : Number(value);
};

const receiptAddress = (location = {}) => {
  const loc = location || {};
  const region = [loc.adr_state, loc.adr_postal_code].filter(Boolean).join(' ');
  return [loc.adr_street, loc.adr_unit, loc.adr_city, region, loc.adr_country]
    .filter(Boolean)
    .join(', ');
};

const cancellationPolicy = receipt => {
  const listing = (receipt && receipt.listing) || {};
  const eligibility = receipt && receipt.refund_eligibility;
  return eligibility != null
    ? {
        ...listing,
        refund_policy: normalizeRefundPolicy(eligibility.policy),
        refund_policy_custom: eligibility.policy_custom
      }
    : {
        ...listing,
        refund_policy: normalizeRefundPolicy(listing.refund_policy)
      };
};

const checklistSummary = items => {
  const required = items.filter(item => !item.informational);
  const complete = required.filter(item => item.status === STATUS.complete)
    .length;
  const actions = items.filter(item => item.guestActionRequired).length;
  const unknown = items.some(item =>
    [STATUS.unavailable, STATUS.contactManager].includes(item.status)
  );
  let heading = unknown ? 'status_unavailable' : 'all_set_title';
  if (actions) heading = actions === 1 ? 'actions_one' : 'actions_many';
  else if (!unknown && complete < required.length) heading = 'nothing_needed';
  return {
    required: required.length,
    complete,
    actions,
    heading,
    allSet: !actions && !unknown && complete === required.length
  };
};

const depositStatus = (receipt = {}, depositState, isResolving = false) => {
  if (
    depositState &&
    (depositState.guestActionRequired ||
      [STATUS.failed, STATUS.actionRequired].includes(depositState.status))
  )
    return 'action_needed';
  const charge = depositCharge(receipt.charges);
  if (charge) return charge.status === 'auth' ? 'authorized' : 'charged';
  if ((receipt.securityDeposit || {}).status === 'collected')
    return 'collected';
  if (depositState && depositState.status === STATUS.complete)
    return 'complete';
  if (
    (depositState && depositState.status === STATUS.scheduled) ||
    (!depositState && !isResolving)
  )
    return 'scheduled';
  return 'pending';
};

const showDeposit = (receipt = {}, depositState) =>
  depositState
    ? depositState.status !== STATUS.notRequired
    : Boolean(
        receipt.securityDeposit &&
          (receipt.securityDeposit.applies || receipt.securityDepositRequired)
      );

const depositCompleteDescriptionKey = status =>
  status === 'collected' ? 'cx.receipt.security_deposit_collected' : null;

const paymentTerms = (receipt = {}, paymentState) => {
  if (paymentState) {
    const scheduled = paymentState.status === STATUS.scheduled;
    return {
      dueDate: scheduled ? paymentState.scheduledDate : paymentState.dueDate,
      amount: [STATUS.dueToday, STATUS.scheduled].includes(paymentState.status)
        ? Number(paymentState.amount || 0)
        : 0,
      scheduled,
      autoCollected: scheduled
    };
  }
  return {
    dueDate: receipt.payment_balance_due_on,
    amount: Number(receipt.payment_due_today || 0),
    scheduled: balanceDueIsInFuture(receipt.payment_balance_due_on),
    autoCollected: Boolean(receipt.payment_auto_collected)
  };
};

const paymentDueToday = (
  receipt = {},
  paymentState,
  lifecycle,
  isResolving
) => {
  if (
    isResolving ||
    ['completed', 'cancelled', 'declined', 'expired'].includes(lifecycle)
  )
    return false;
  if (paymentState)
    return (
      paymentState.status === STATUS.dueToday &&
      Boolean(paymentState.guestActionRequired)
    );
  const terms = paymentTerms(receipt);
  return terms.amount > 0 && !terms.scheduled;
};

const showMobilePay = (receipt = {}, paymentState, lifecycle, isResolving) => {
  const booking = receipt.booking || {};
  const remaining =
    booking.price_remaining == null
      ? Number(booking.price_total || 0) - Number(booking.price_paid || 0)
      : Number(booking.price_remaining);
  return Boolean(
    booking.confirmed &&
      !booking.cancelled &&
      Number(receipt.payment_due_today) > 0 &&
      remaining > 0 &&
      paymentDueToday(receipt, paymentState, lifecycle, isResolving)
  );
};

const isChannelCollected = (readiness, paymentState) =>
  Boolean(
    (readiness &&
      readiness.payment &&
      (readiness.payment.collection_method === 'channel' ||
        readiness.payment.channel_collected === true)) ||
      (paymentState &&
        paymentState.status === STATUS.notRequired &&
        paymentState.informational)
  );

const nightlyRateKey = nights =>
  Number(nights) === 1 ? 'nightly_rate_one' : 'nightly_rate';

const paymentActivity = ({ charges, refunds, pricePaid } = {}) => {
  const timestamp = charge => {
    if (!charge.created_at) return -Infinity;
    const value = moment.parseZone(
      charge.created_at,
      [moment.ISO_8601, 'MM/DD/YYYY HH:mm:ss'],
      true
    );
    return value.isValid() ? value.valueOf() : -Infinity;
  };
  return {
    charges: settledGuestCharges(charges)
      .slice()
      .sort((left, right) => timestamp(right) - timestamp(left)),
    refunded: refundTotal({ charges, refunds, pricePaid })
  };
};

const verificationGroup = (requirements, authenticate = false) => {
  if (!requirements) return null;
  const keys = [REQUIREMENT.verification, REQUIREMENT.signature];
  const hasReadinessRow = keys.some(key => requirements[key]);
  if (!authenticate && !hasReadinessRow) return null;
  const parts = hasReadinessRow
    ? keys
        .map(key => requirements[key] || { key, status: STATUS.unavailable })
        .filter(item => item.status !== STATUS.notRequired)
    : [];
  if (!parts.length && !authenticate) return null;
  const primary =
    (authenticate && {
      status: STATUS.actionRequired,
      guestActionRequired: true
    }) ||
    parts.find(item => item.guestActionRequired) ||
    parts.find(item =>
      [STATUS.unavailable, STATUS.contactManager].includes(item.status)
    ) ||
    parts.find(item => item.status !== STATUS.complete) ||
    parts[0];
  return {
    ...primary,
    key: REQUIREMENT.verification,
    guestActionRequired:
      authenticate || parts.some(item => item.guestActionRequired),
    parts
  };
};

const checklistItems = (stayState, authenticate = false) => {
  const { requirements, actionable } = stayState;
  return [
    requirements[REQUIREMENT.payment],
    verificationGroup(requirements, authenticate),
    requirements[REQUIREMENT.securityDeposit],
    requirements[REQUIREMENT.guestInformation]
  ].filter(
    item =>
      item &&
      (actionable
        ? item.status !== STATUS.notRequired || item.informational
        : [STATUS.unavailable, STATUS.contactManager].includes(item.status))
  );
};

const showChecklistCard = stayState => {
  if (!stayState) return false;
  if (
    [
      'request_pending',
      'completed',
      'cancelled',
      'declined',
      'expired'
    ].includes(stayState.lifecycle)
  )
    return false;
  return (
    stayState.lifecycle === 'in_progress' ||
    stayState.actionable ||
    checklistItems(stayState).length > 0
  );
};

module.exports = {
  receiptTheme,
  verificationPrompts,
  receiptPlaceholders,
  depositCharge,
  depositAmount,
  receiptAddress,
  cancellationPolicy,
  checklistSummary,
  depositStatus,
  showDeposit,
  depositCompleteDescriptionKey,
  paymentTerms,
  paymentDueToday,
  showMobilePay,
  isChannelCollected,
  nightlyRateKey,
  paymentActivity,
  verificationGroup,
  checklistItems,
  showChecklistCard
};
